import { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Upload, Pencil, X, Check } from 'lucide-react';
import { apiClient } from '@/api/client';

const TYPES = ['NICK_COLOR', 'NICK_ICON', 'FRIDGE_SKIN', 'STICKER_SKIN', 'MAGNET_SKIN', 'GIFT', 'BYPASS_FRIEND'] as const;
type ItemType = typeof TYPES[number];

interface ShopItem {
  id: string;
  shopId: string;
  type: string;
  name: string;
  description: string | null;
  price: number;
  previewUrl: string | null;
  metadata: Record<string, unknown>;
  stock: number | null;
  active: boolean;
  createdAt: string;
  shop?: { id: string; name: string };
}

interface ItemDraft {
  id?: string;
  type: ItemType;
  name: string;
  description: string;
  price: number;
  metadataJson: string;
  stock: string; // string to allow empty
  active: boolean;
}

const emptyDraft = (): ItemDraft => ({
  type: 'NICK_COLOR',
  name: '',
  description: '',
  price: 1,
  metadataJson: '{}',
  stock: '',
  active: true,
});

const apiBase = ((import.meta as any).env?.VITE_API_URL ?? '').replace(/\/$/, '');

function previewUrlToSrc(p: string | null): string | undefined {
  if (!p) return undefined;
  if (/^https?:\/\//i.test(p)) return p;
  if (p.startsWith('/')) return `${apiBase}${p}`;
  return p;
}

export default function AdminShop() {
  const [items, setItems] = useState<ShopItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<ItemType | 'ALL'>('ALL');
  const [editingDraft, setEditingDraft] = useState<ItemDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTargetId, setUploadTargetId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = filterType === 'ALL' ? '' : `?type=${filterType}`;
      const res = await apiClient.get<ShopItem[]>(`/api/admin/shop/items${qs}`);
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterType]);

  // Ensure system shop exists on page load (idempotent endpoint)
  useEffect(() => {
    apiClient.get('/api/admin/shop/system-shop').catch(() => undefined);
  }, []);

  const startCreate = () => { setEditingDraft(emptyDraft()); setError(null); };
  const startEdit = (item: ShopItem) => {
    setEditingDraft({
      id: item.id,
      type: item.type as ItemType,
      name: item.name,
      description: item.description ?? '',
      price: item.price,
      metadataJson: JSON.stringify(item.metadata ?? {}, null, 2),
      stock: item.stock == null ? '' : String(item.stock),
      active: item.active,
    });
    setError(null);
  };

  const save = async () => {
    if (!editingDraft) return;
    setSaving(true);
    setError(null);
    try {
      let metadata: Record<string, unknown> = {};
      try {
        metadata = JSON.parse(editingDraft.metadataJson || '{}');
      } catch {
        throw new Error('Неверный JSON в metadata');
      }
      const body: Record<string, unknown> = {
        type: editingDraft.type,
        name: editingDraft.name.trim(),
        description: editingDraft.description.trim() || null,
        price: Number(editingDraft.price) || 1,
        metadata,
        stock: editingDraft.stock === '' ? null : Number(editingDraft.stock),
        active: editingDraft.active,
      };
      if (editingDraft.id) {
        await apiClient.put(`/api/admin/shop/items/${editingDraft.id}`, body);
      } else {
        await apiClient.post('/api/admin/shop/items', body);
      }
      setEditingDraft(null);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.message ?? 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Удалить товар? Это не удалит уже купленные экземпляры у пользователей.')) return;
    await apiClient.delete(`/api/admin/shop/items/${id}`);
    await load();
  };

  const triggerUpload = (id: string) => {
    setUploadTargetId(id);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const id = uploadTargetId;
    e.target.value = '';
    setUploadTargetId(null);
    if (!file || !id) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await apiClient.post(`/api/admin/shop/items/${id}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (err: any) {
      alert(err.response?.data?.message ?? 'Ошибка загрузки');
    }
  };

  return (
    <div className="p-4 max-w-5xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-surface-100">Магазин — каталог</h2>
        <button
          onClick={startCreate}
          className="px-3 py-1.5 rounded bg-brand-600 hover:bg-brand-500 text-white text-sm flex items-center gap-1.5"
        >
          <Plus size={14} /> Добавить товар
        </button>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <label className="text-surface-400 text-sm">Фильтр:</label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as ItemType | 'ALL')}
          className="bg-surface-800 border border-surface-700 rounded px-2 py-1 text-surface-100 text-sm"
        >
          <option value="ALL">Все типы</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-surface-500 text-sm">Загрузка...</p>
      ) : items.length === 0 ? (
        <p className="text-surface-500 text-sm">Нет товаров. Создайте первый.</p>
      ) : (
        <div className="bg-surface-900 border border-surface-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-800 text-surface-400 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Превью</th>
                <th className="text-left px-3 py-2">Тип</th>
                <th className="text-left px-3 py-2">Название</th>
                <th className="text-right px-3 py-2">Цена</th>
                <th className="text-left px-3 py-2">Metadata</th>
                <th className="text-center px-3 py-2">Акт.</th>
                <th className="text-right px-3 py-2">Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const preview = previewUrlToSrc(item.previewUrl);
                const color = (item.metadata as any)?.color as string | undefined;
                return (
                  <tr key={item.id} className="border-t border-surface-800">
                    <td className="px-3 py-2">
                      {preview ? (
                        <img src={preview} alt="" className="w-10 h-10 rounded object-cover" />
                      ) : color ? (
                        <div className="w-10 h-10 rounded" style={{ background: color }} />
                      ) : (
                        <div className="w-10 h-10 rounded bg-surface-800 border border-dashed border-surface-700" />
                      )}
                    </td>
                    <td className="px-3 py-2 text-surface-300 font-mono text-xs">{item.type}</td>
                    <td className="px-3 py-2 text-surface-100">
                      {item.name}
                      {item.description && (
                        <span className="block text-xs text-surface-500">{item.description}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-surface-300 font-mono">{item.price} SWIPA</td>
                    <td className="px-3 py-2 text-xs text-surface-500 max-w-[200px] truncate">
                      {JSON.stringify(item.metadata)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {item.active ? <Check size={14} className="inline text-emerald-400" /> : <X size={14} className="inline text-red-400" />}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => triggerUpload(item.id)}
                        className="p-1 text-surface-400 hover:text-brand-400"
                        title="Загрузить превью"
                      >
                        <Upload size={14} />
                      </button>
                      <button
                        onClick={() => startEdit(item)}
                        className="p-1 text-surface-400 hover:text-brand-400"
                        title="Редактировать"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => remove(item.id)}
                        className="p-1 text-red-400 hover:text-red-300"
                        title="Удалить"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Modal: create / edit */}
      {editingDraft && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setEditingDraft(null)}
        >
          <div
            className="bg-surface-900 border border-surface-700 rounded-xl w-full max-w-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-surface-100 font-semibold">
                {editingDraft.id ? 'Редактировать' : 'Новый'} товар
              </h3>
              <button onClick={() => setEditingDraft(null)} className="p-1 text-surface-400 hover:text-surface-200">
                <X size={16} />
              </button>
            </div>

            {error && (
              <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded px-3 py-2">
                {error}
              </div>
            )}

            <div>
              <label className="text-surface-400 text-xs block mb-1">Тип</label>
              <select
                value={editingDraft.type}
                onChange={(e) => setEditingDraft({ ...editingDraft, type: e.target.value as ItemType })}
                className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-sm"
              >
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <label className="text-surface-400 text-xs block mb-1">Название</label>
              <input
                value={editingDraft.name}
                onChange={(e) => setEditingDraft({ ...editingDraft, name: e.target.value })}
                className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-sm"
                placeholder="Например: Огонь"
              />
            </div>

            <div>
              <label className="text-surface-400 text-xs block mb-1">Описание (опционально)</label>
              <textarea
                value={editingDraft.description}
                onChange={(e) => setEditingDraft({ ...editingDraft, description: e.target.value })}
                rows={2}
                className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-surface-400 text-xs block mb-1">Цена (SWIPA)</label>
                <input
                  type="number"
                  min={1}
                  value={editingDraft.price}
                  onChange={(e) => setEditingDraft({ ...editingDraft, price: Number(e.target.value) })}
                  className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-sm"
                />
              </div>
              <div>
                <label className="text-surface-400 text-xs block mb-1">Запас (пусто = ∞)</label>
                <input
                  type="number"
                  min={0}
                  value={editingDraft.stock}
                  onChange={(e) => setEditingDraft({ ...editingDraft, stock: e.target.value })}
                  className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="text-surface-400 text-xs block mb-1">
                Metadata (JSON). Для NICK_COLOR: <code>{'{"color":"#rrggbb"}'}</code>. Для NICK_ICON — url уже в previewUrl.
              </label>
              <textarea
                value={editingDraft.metadataJson}
                onChange={(e) => setEditingDraft({ ...editingDraft, metadataJson: e.target.value })}
                rows={4}
                className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-xs font-mono"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editingDraft.active}
                onChange={(e) => setEditingDraft({ ...editingDraft, active: e.target.checked })}
                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500"
              />
              <span className="text-surface-200 text-sm">Активен (виден пользователям)</span>
            </label>

            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setEditingDraft(null)} className="btn-ghost">Отмена</button>
              <button
                onClick={save}
                disabled={saving || !editingDraft.name.trim()}
                className="btn-primary"
              >
                {saving ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </div>
            {editingDraft.id && (
              <p className="text-xs text-surface-500 text-center pt-1">
                Превью (иконка/цвет) загружается после сохранения через кнопку <Upload size={10} className="inline" />
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
